import type { SharedLibHandler } from '@repo/common/types/SharedLib';
import { get } from 'svelte/store';
import { getHandler, getHandlerAsync } from '../sharedlib';
import {
  isSignedIn,
  setUser,
  setUserAuth,
  userAuthModel,
  userAuthStore,
  userDataModel
} from './auth.store';

export const waitForAuth = async (timeoutMs: number = 5000) => {
  // Check current state first - if already available, return immediately
  const currentAuth = get(userAuthModel);
  if (currentAuth.item?.idToken && currentAuth.item?.refreshToken) {
    return;
  }

  return new Promise<void>((resolve, reject) => {
    let unsubscribe: () => void = () => {};
    let resolved = false;

    const checkCondition = (dataModel: any) => {
      if (resolved) return;
      const result = !!dataModel.item.idToken && !!dataModel.item.refreshToken;
      if (result) {
        resolved = true;
        unsubscribe?.();
        resolve();
      }
    };

    unsubscribe = userAuthModel.subscribe(checkCondition);

    // Set up timeout
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        unsubscribe?.();
        reject(new Error(`Auth tokens not available after waiting ${timeoutMs}ms`));
      }
    }, timeoutMs);
  });
};

const waitForUserLoaded = async (timeoutMs: number = 5000) => {
  // Check current state first - if already loaded, return immediately
  const currentUserData = get(userDataModel);
  if (currentUserData.status === 'loaded') {
    return;
  }

  return new Promise<void>((resolve, reject) => {
    let unsubscribe: () => void = () => {};
    let resolved = false;

    const checkCondition = (dataModel: any) => {
      if (resolved) return;
      if (dataModel.status === 'loaded') {
        resolved = true;
        unsubscribe?.();
        resolve();
      }
    };

    unsubscribe = userDataModel.subscribe(checkCondition);

    // Set up timeout
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        unsubscribe?.();
        reject(new Error(`User data not loaded after waiting ${timeoutMs}ms`));
      }
    }, timeoutMs);
  });
};

export const waitForAuthAndUserId = async (timeoutMs: number = 5000) => {
  const startTime = Date.now();

  // Wait for both auth tokens and user ID to be set
  await waitForAuth(timeoutMs);
  await waitForUserLoaded(timeoutMs);

  // Calculate remaining timeout after auth/user waits
  const elapsedAfterAuth = Date.now() - startTime;
  const remainingTimeout = Math.max(1000, timeoutMs - elapsedAfterAuth);

  // Try to get handler - use a shorter timeout to prevent hanging
  let handler: SharedLibHandler;
  try {
    // Use a shorter timeout for getHandlerAsync to prevent indefinite hangs
    handler = await Promise.race([
      getHandlerAsync(),
      new Promise<SharedLibHandler>((_, reject) =>
        setTimeout(
          () => reject(new Error('Handler initialization timeout')),
          Math.min(remainingTimeout, 5000)
        )
      )
    ]);
  } catch (error) {
    // If handler isn't ready, try to get it synchronously (might throw)
    try {
      handler = getHandler();
    } catch (syncError) {
      throw new Error(
        `Handler not available: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  // Quick check first - if already set, return immediately
  if (handler.hasUserId()) {
    return;
  }

  // If user data is already loaded, extract the user ID and set it on the handler
  // This fixes a race condition where userDataModel status becomes 'loaded'
  // before handler.setUserId() is called
  const $userDataModel = get(userDataModel);
  if ($userDataModel.status === 'loaded' && $userDataModel.item?.id) {
    console.debug('User data loaded but handler missing user ID, setting it now');
    handler.setUserId($userDataModel.item.id);
    return;
  }

  const retryDelay = 100;
  const handlerWaitStartTime = Date.now();

  while (!handler.hasUserId()) {
    const elapsedTime = Date.now() - handlerWaitStartTime;

    if (elapsedTime >= remainingTimeout) {
      throw new Error(`User ID not set in handler after waiting ${remainingTimeout}ms`);
    }

    // Check again if user data became available during the wait
    const $userDataModel = get(userDataModel);
    if ($userDataModel.status === 'loaded' && $userDataModel.item?.id) {
      handler.setUserId($userDataModel.item.id);
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, retryDelay));
  }
};
// Fast version for navigation - assumes user is already authenticated

export const waitForAuthAndUserIdFast = async () => {
  const handler = await getHandlerAsync();

  // If user ID is already set, return immediately
  if (handler.hasUserId()) {
    return;
  }

  // Quick check if we're already signed in
  const { get } = await import('svelte/store');
  const $isSignedIn = get(isSignedIn);
  if ($isSignedIn) {
    return;
  }

  // Otherwise fall back to full wait
  await waitForAuthAndUserId().catch((err) => {
    console.debug('Error waiting for auth and user id:', err);
    return;
  });
};
// Function to update onboarding step index

export const updateOnboardingStepIndex = async (stepIndex: number) => {
  const $user = get(userDataModel)?.item;
  const $userAuth = get(userAuthStore);

  if (!$userAuth?.idToken) {
    throw Error('Missing userAuth idToken');
  }

  if (!$user) {
    throw Error('User not loaded');
  }

  // Update the user with the new step index in the profile field
  const updatedProfile = {
    ...$user.profile,
    currentOnboardingStepIndex: stepIndex
  };

  // Import patchUser function
  const { patchUser } = await import('../data/user.data');

  // Patch the user in the database with the updated profile
  const result = await patchUser({ profile: updatedProfile }, $userAuth);

  // Update the local store with the result
  await setUser(result, true, 'loaded', false);

  console.log(`Updated onboarding step index to ${stepIndex}`);
};
if (typeof window !== 'undefined') {
  // @ts-expect-error - adding custom window method.
  window.setUser = setUser;
}

export const loadUserWeb = async () => {
  const response = await fetch('/api/private/user');
  if (!response.ok) {
    throw new Error('Failed to load user');
  }

  // Extract auth tokens from response headers
  const idToken = response.headers.get('x-access-token');
  const refreshToken = response.headers.get('x-refresh-token');
  const apiToken = response.headers.get('x-api-token');
  const apiTokenM = response.headers.get('x-api-token-m');

  // Set auth tokens if present
  if (idToken && refreshToken) {
    const authTokens = {
      idToken,
      refreshToken,
      ...(apiToken && { apiToken }),
      ...(apiTokenM && { apiTokenM })
    };
    await setUserAuth(authTokens as any, false, 'loaded', false);
  }

  const user = await response.json();
  setUser(user);
  const handler = getHandler();
  handler.setUserId(user.id);
  userDataModel.update((dataModel) => {
    dataModel.item = user;
    dataModel.status = 'loaded';
    return dataModel;
  });
};
